import { Alert, IconButton } from '@mui/material';
import { MdSend } from 'react-icons/md';
import { useForm } from 'react-hook-form';
import ChatEmojis from '../../chatEmojis';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { MessageContext } from '.';
import { MeetingChatMessage } from '../../../../interfaces/interfaces';
import { useSelector } from '../../../../reduxToolkit/typedRedux';

export interface InputStyle {
  input?: string;
  inputSx?: any;
  icon?: string;
  div?: string;
}

export interface MessageInputProps {
  canSend?: boolean;
  messageTimeout?: number;
  style?: InputStyle;
  onSendMessage: (message: string, replyTo?: MeetingChatMessage) => void;
}

export const MessageInput = ({ canSend = true, messageTimeout, style, onSendMessage }: MessageInputProps) => {
  const isBannedFromChat = useSelector((state) => state.chat.isBannedFromChat);
  const { handleSubmit, reset, register, setValue } = useForm();
  const { onChange, onBlur, name, ref } = register('message');

  const { replyTo, setReplyTo } = useContext(MessageContext);

  const { t } = useTranslation();

  const [timeLeft, setTimeLeft] = useState(0);

  const inputRef = useRef<any>(ref);

  async function handleSend(props: any) {
    if (timeLeft > 0) return;
    if (!canSend || isBannedFromChat) return;

    onSendMessage(props.message, replyTo);

    reset({ message: '' });
    setReplyTo(undefined);
    if (messageTimeout && messageTimeout > 1000) setTimeLeft(messageTimeout / 1000);
  }

  useEffect(() => {
    if (!timeLeft) return;
    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  return (
    <form className={`w-full flex items-center gap-4 mt-2 ${style?.div}`} onSubmit={handleSubmit(handleSend)}>
      {!isBannedFromChat ? (
        <>
          <input
            onChange={onChange}
            disabled={timeLeft > 0}
            onBlur={onBlur}
            name={name}
            ref={ref}
            required
            className="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            placeholder={timeLeft > 0 ? t('chat:chat-blocked-for-x-seconds', { seconds: timeLeft }) : t('chat:type-a-message')}
            maxLength={255}
            autoComplete="off"
          />
          <ChatEmojis
            chatInputRef={inputRef}
            bottom={60}
            iconColor={style?.icon}
            onChange={(value) => {
              setValue('message', value);
            }}
          />
          <IconButton type="submit" className={`w-10 h-10 ${style?.icon}`}>
            <MdSend />
          </IconButton>
        </>
      ) : (
        <Alert severity="error" className="w-full">
          {t('event:stage.banned-text')}
        </Alert>
      )}
    </form>
  );
};
